import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { GridColumnVisibilityModel } from '@mui/x-data-grid';
import { createScopedJSONStorage, scopedLocalStorage } from '@/lib/browser/scopedStorage';
import type { ProjectSearchCriteria } from '@/api/ApiClient';
import { enforceProjectGridNonHideableColumns } from '@/lib/constants/projectGridColumnGroups';
import { log } from '@/lib/utils/logger';

/** Storage key used for persisting project filters in localStorage */
const STORAGE_KEY = 'projectFilters';

/** Storage key for column visibility */
export const COLUMN_VISIBILITY_STORAGE_KEY = 'projectColumnVisibility';

/**
 * Default visibility for project search grid columns (fields match GridColDef.field).
 * Merged with localStorage so new columns get sensible defaults when prefs are stale.
 */
export const defaultProjectColumnVisibilityModel: GridColumnVisibilityModel = {
  projectLabel: true,
  title: true,
  user: true,
  projectStatus: true,
  faculty: false,
  staff: true,
  materials: true,
  techniques: true,
  institutionType: true,
  // Project details — default hidden (toolbar “Columns” / “Groups” can show)
  type: false,
  piInstitution: false,
  piState: false,
  startDate: false,
  endDate: false,
  // Samples — default visible (individual columns also gated by API / privileges)
  totalNbSamplesRequested: true,
  nbSamplesShipped: true,
  totalNbSamples: true,
  nbSamplesDue: true,
  // Last changed — default hidden
  lastChangedDate: false,
  lastChangedBy: false,
};

/** Default filter criteria for project search */
export const defaultProjectFilterCriteria: ProjectSearchCriteria = {
  synTechniqueId: '',
  ownProjectsOnly: false,
  privilege: '',
  operation: '',
  year: undefined,
  status: undefined,
  statusEnum: undefined,
  type: '',
  includeInternal: undefined,
  includeExternal: undefined,
  samplesDueBy: '',
  prioToProjectsWithSamples: false,
  materials: [],
  elements: [],
  searchText: '',
  state: '',
  institution: '',
};

function valueDiffers(a: unknown, b: unknown): boolean {
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return true;
    return a.some((v, i) => v !== b[i]);
  }
  if (a instanceof Date && b instanceof Date) return a.getTime() !== b.getTime();
  if (a instanceof Date || b instanceof Date) return (a == null) !== (b == null);
  // Treat empty string and undefined/null as equivalent for unset optional fields
  if ((a === '' || a == null) && (b === '' || b == null)) return false;
  // Treat undefined/null/false as equivalent for optional booleans
  if (typeof b === 'boolean') {
    const aFalsy = a === undefined || a === null || a === false;
    if (b === false) return !aFalsy;
    return a !== true;
  }
  return a !== b;
}

/**
 * Returns true if any non-basic filter is set so the Filter button should show a dot.
 * The quick-row searchText is treated as basic and does not trigger the dot.
 */
export function hasProjectExtendedFiltersSet(filters: ProjectSearchCriteria): boolean {
  const def = defaultProjectFilterCriteria;
  const keys = Object.keys(def) as (keyof ProjectSearchCriteria)[];

  for (const key of keys) {
    const a = filters[key];
    const b = def[key];
    if (!valueDiffers(a, b)) continue;

    // searchText, ownProjectsOnly, type live in the quick row and should not light the dot
    if (key === 'searchText' || key === 'ownProjectsOnly' || key === 'type') continue;
    return true;
  }
  return false;
}

/**
 * Load stored column visibility from localStorage
 */
export const loadStoredColumnVisibility = (): GridColumnVisibilityModel => {
  if (typeof window === 'undefined') {
    return enforceProjectGridNonHideableColumns({ ...defaultProjectColumnVisibilityModel });
  }

  try {
    const stored = scopedLocalStorage.getItem(COLUMN_VISIBILITY_STORAGE_KEY);
    if (!stored) {
      return enforceProjectGridNonHideableColumns({ ...defaultProjectColumnVisibilityModel });
    }
    const merged = { ...defaultProjectColumnVisibilityModel, ...JSON.parse(stored) } as GridColumnVisibilityModel;
    return enforceProjectGridNonHideableColumns(merged);
  } catch (error) {
    log.warn('Failed to load stored column visibility:', error);
    return enforceProjectGridNonHideableColumns({ ...defaultProjectColumnVisibilityModel });
  }
};

/**
 * Interface defining the project filter store state and actions
 */
interface ProjectFilterStore {
  /** Current filter criteria for project search */
  filters: ProjectSearchCriteria;
  /** True while the project grid is actively fetching results */
  isSearching: boolean;
  /** Sets search-in-progress state (not persisted) */
  setIsSearching: (isSearching: boolean) => void;
  /** Updates the filter criteria and persists to localStorage */
  setFilters: (filters: ProjectSearchCriteria) => void;
  /** Resets filters to default criteria and removes from localStorage */
  resetFilters: () => void;
  /** Current pagination state */
  paginationModel: { page: number; pageSize: number };
  /** Updates pagination state, accepts either new state or updater function */
  setPaginationModel: (
    model:
      | { page: number; pageSize: number }
      | ((prev: { page: number; pageSize: number }) => { page: number; pageSize: number })
  ) => void;
}

/**
 * Zustand store for managing project filters and pagination
 * Now uses Zustand's persist middleware for automatic localStorage persistence.
 */
export const useProjectFilterStore = create<ProjectFilterStore>()(
  persist(
    (set, get) => ({
      filters: defaultProjectFilterCriteria,
      isSearching: false,
      setIsSearching: (isSearching) => set({ isSearching }),
      setFilters: (filters) => set({ filters }),
      resetFilters: () => set({ filters: defaultProjectFilterCriteria }),
      paginationModel: { page: 0, pageSize: 25 },
      setPaginationModel: (modelOrUpdater) => {
        const current = get().paginationModel;
        const newModel = typeof modelOrUpdater === 'function' ? modelOrUpdater(current) : modelOrUpdater;
        set({ paginationModel: newModel });
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createScopedJSONStorage(),
      partialize: (state) => ({ filters: state.filters, paginationModel: state.paginationModel }),
    }
  )
);
